import * as React from "react";
import { locKeys } from "../l10n";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { Dates } from "src/components/shared/Dates/Dates";
import { TimeDropdown } from "src/components/shared/TimeDropdown/TimeDropdown";
import { GTHiddenSearchData } from "./GTHiddenSearchData";
import { BookRoundtrip } from "./BookRoundtrip";
import { observer } from "mobx-react";
import { GroundTransportLocation } from "./Location/GroundTransportLocation";
import { BookRoundtripToggle } from "./BookRoundtripToggle";
import { CarWizardState } from "src/stores/wizard/state/car/CarWizardState";

interface GroundTransportationPWAViewProps {
  carWizardState: CarWizardState;
  toggleBookRoundtrip: () => void;
  setInputsRefs?: (ref: React.RefObject<HTMLInputElement>) => void;
  getLocError: (isLink: boolean) => string | undefined;
}

export const GroundTransportPWAView: React.FunctionComponent<GroundTransportationPWAViewProps> = observer((props) => {
  const {
    carWizardState,
    carWizardState: {
      pickupTime,
      isGroundTransportFormValid,
      returnTime,
      roundTrip,
      groundTransportationLocationState,
      updateInvalidFormsState,
    },
    setInputsRefs,
    toggleBookRoundtrip,
  } = props;
  const { gtArrivalTime } = locKeys;
  const { isOriginAirport } = groundTransportationLocationState;
  const format12hrConfig = carWizardState.config.timeFormats.timeFormat12Hour;

  const mainGridLayout: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 12 };

  const locationColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12 };

  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 4 };
  const datesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 4 };
  const timeDropdownColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 4 };
  const errorSummaryGridColumns: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };

  const roundTripDatesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 3 };
  const roundTripTimeColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 3 };

  if (isGroundTransportFormValid) {
    updateInvalidFormsState();
  }

  return (
    <>
      <UitkLayoutGrid columns={mainGridLayout} space="three">
        <>
          {!isGroundTransportFormValid && (
            <LobErrorSummary
              locHeadingToken={props.getLocError(false)!}
              locHeadingArgs={carWizardState.moreThanOneError() && [carWizardState.numberOfErrors]}
              locLinkToken={props.getLocError(true)}
              linkClickFocusId="id-roundtrip-error"
              inputErrorRef={carWizardState.errorInputRef}
              colSpan={errorSummaryGridColumns}
              setInputsRefs={setInputsRefs}
            />
          )}

          <GroundTransportLocation
            groundTransportationLocationState={groundTransportationLocationState}
            colSpan={locationColSpan}
            setInputsRefs={setInputsRefs}
            lobState={carWizardState}
          />
          <Travelers lobState={carWizardState} colSpan={travelersColSpan} />
          <Dates lobState={carWizardState} singleDate={true} colSpan={datesColSpan} setInputsRefs={setInputsRefs} />
          <TimeDropdown
            labelKey={gtArrivalTime}
            timeValues={carWizardState.config.gtConfig.timeValues}
            onChange={carWizardState.setPickupTime}
            value={pickupTime}
            invalidKey={carWizardState.timeStartInvalidKey}
            colSpan={timeDropdownColSpan}
            use12hr={format12hrConfig}
          />
          {isOriginAirport && (
            <BookRoundtripToggle toggleBookRoundtrip={toggleBookRoundtrip} roundTripChecked={roundTrip} />
          )}
          {isOriginAirport && roundTrip && (
            <BookRoundtrip
              carWizardState={carWizardState}
              datesColSpan={roundTripDatesColSpan}
              timeColSpan={roundTripTimeColSpan}
            />
          )}
        </>
      </UitkLayoutGrid>
      <SubmitRow lobState={props.carWizardState} />
      <GTHiddenSearchData
        pickUpTime={pickupTime}
        dropOffTime={roundTrip ? returnTime : ""}
        direction={isOriginAirport}
      />
    </>
  );
});
